import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Users,
  Workflow,
  MessageSquare,
  Calendar,
  TrendingUp,
  Globe,
  Star,
  BarChart3,
  Bot,
  FolderKanban
} from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: Users,
      title: 'Unlimited Contacts & Sub-Accounts',
      description: 'Manage unlimited contacts and client accounts without restrictions or hidden fees.',
    },
    {
      icon: FolderKanban,
      title: 'Visual Pipeline Management',
      description: 'Track leads and deals with intuitive Kanban-style pipelines and customizable dashboards.',
    },
    {
      icon: Workflow,
      title: 'Workflow Automation',
      description: 'Build powerful automated workflows to nurture leads and streamline your processes.',
    },
    {
      icon: MessageSquare,
      title: 'Multi-Channel Campaigns',
      description: 'Run campaigns across SMS, email, voicemail, Facebook messages, and calls from one platform.',
    },
    {
      icon: Globe,
      title: 'Website & Funnel Builder',
      description: 'Create stunning websites, landing pages, and sales funnels with drag-and-drop simplicity.',
    },
    {
      icon: Calendar,
      title: 'Appointment Scheduling',
      description: 'Integrated calendars for seamless booking, reducing no-shows and scheduling conflicts.',
    },
    {
      icon: Star,
      title: 'Reputation Management',
      description: 'Monitor and respond to reviews while boosting your local SEO and online presence.',
    },
    {
      icon: Bot,
      title: 'AI Agent Tools',
      description: 'Leverage AI-driven agents for voice calls, workflow automation, and customer interactions.',
    },
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Sophisticated reporting to monitor campaign performance, sales, and engagement metrics.',
    },
    {
      icon: TrendingUp,
      title: 'Membership Sites',
      description: 'Build and manage membership areas with gated content for recurring revenue streams.',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-[#0f3a3a] to-[#0a2e2e] relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #8b956d 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#c5c9b8] mb-4 font-['Cinzel']">
            Powerful Features
          </h2>
          <p className="text-lg md:text-xl text-[#8b956d] max-w-3xl mx-auto">
            Everything you need to manage, automate, and scale your business—all in one legendary platform.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="bg-[#1a4d4d]/30 backdrop-blur-sm border-[#8b956d]/20 hover:border-[#8b956d]/50 transition-all duration-300 hover:shadow-2xl hover:shadow-black/60 hover:scale-105 group"
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-[#8b956d]/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#8b956d]/30 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-[#8b956d]" />
                  </div>
                  <CardTitle className="text-xl text-[#c5c9b8] font-semibold">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-[#b5bca0] leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
